import { Controller, useForm } from 'react-hook-form';
import { Box } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';

import asBentoPage from 'common/bento/hoc/asBentoPage';
import Button from 'components/_core/Button';
import Callout from 'components/_core/Callout';
import * as Form from 'components/_core/form';
import Header from 'features/Onboarding/components/Header';
import OBNavigation from 'features/Onboarding/components/OBNavigation/OBNavigation';

import capitalDepositEUBankAccountSchema, {
  CapitalDepositEUBankAccountSchema,
} from '../../libs/capitalDepositEUBankAccountSchema';
import locales from './locales';

interface CapitalDepositEUBankAccountProps {
  onSubmit: (values: CapitalDepositEUBankAccountSchema) => void;
  isMandatee: boolean;
  defaultValues: Partial<CapitalDepositEUBankAccountSchema>;
  isSubmitting: boolean;
}

const CapitalDepositEUBankAccountForm =
  asBentoPage<CapitalDepositEUBankAccountProps>(
    ({ defaultValues, isMandatee, isSubmitting, Layout, onPrev, onSubmit }) => {
      const {
        control,
        formState: { errors },
        handleSubmit,
      } = useForm<CapitalDepositEUBankAccountSchema>({
        defaultValues,
        mode: 'onChange',
        resolver: zodResolver(capitalDepositEUBankAccountSchema),
      });

      return (
        <Layout nav={<OBNavigation onPrev={onPrev} />}>
          <Box as="form" onSubmit={handleSubmit(onSubmit)}>
            <Header
              title={
                isMandatee ? locales.title.mandatee : locales.title.nonMandatee
              }
            />

            <Form.Field
              error={errors.hasEuropeanBankAccount?.message}
              marginBottom="space-36"
            >
              <Controller
                control={control}
                name="hasEuropeanBankAccount"
                render={({ field: { onChange, value } }) => (
                  <Form.Radio.Group
                    direction="row"
                    isDisabled={isSubmitting}
                    onChange={(val) => onChange(val === 'true')}
                    radioPosition="right"
                    value={value === undefined ? undefined : String(value)}
                    variant="outline"
                  >
                    <Form.Radio value="true">
                      <Form.Radio.Title>{locales.yesResponse}</Form.Radio.Title>
                    </Form.Radio>

                    <Form.Radio value="false">
                      <Form.Radio.Title>{locales.noResponse}</Form.Radio.Title>
                    </Form.Radio>
                  </Form.Radio.Group>
                )}
              />
            </Form.Field>

            <Callout hideIcon marginBottom="space-40">
              <Callout.Content>
                {isMandatee
                  ? locales.helpText.mandatee
                  : locales.helpText.nonMandatee}
              </Callout.Content>
            </Callout>

            <Button isExpanded isLoading={isSubmitting} type="submit">
              {locales.ctaContinue}
            </Button>
          </Box>
        </Layout>
      );
    },
  );

export default CapitalDepositEUBankAccountForm;
